/**
 * SegmentFilterRow - Linha individual de filtro no construtor de segmentos
 * 
 * Permite selecionar tipo de filtro, operador e valores.
 * Filtros de CRM só aparecem se crmEnabled = true.
 * 
 * @author Sistema de Campanhas
 * @version 1.0.0
 */

import { useMemo } from 'react';
import { X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import {
  SegmentFilter,
  SegmentFilterType,
  SegmentOperator,
  FilterOption,
  getAvailableFilterTypes,
  OPERATORS_BY_VALUE_TYPE,
  OPERATOR_LABELS,
  FilterTypeConfig,
} from '@/types/campaign-segments';
import { CampaignSegmentOptionsResult } from '@/hooks/useCampaignSegmentOptions';
import { cn } from '@/lib/utils';

interface SegmentFilterRowProps {
  filter: SegmentFilter;
  onChange: (filter: SegmentFilter) => void;
  onRemove: () => void;
  options: CampaignSegmentOptionsResult;
  crmEnabled: boolean;
  isOnlyFilter?: boolean;  // Se é o único filtro do grupo (não pode remover)
}

/**
 * Componente de linha de filtro individual
 */
export function SegmentFilterRow({
  filter,
  onChange,
  onRemove,
  options,
  crmEnabled,
  isOnlyFilter = false,
}: SegmentFilterRowProps) {
  // Tipos de filtros disponíveis baseado em crmEnabled
  const availableFilterTypes = useMemo(() => {
    return getAvailableFilterTypes(crmEnabled);
  }, [crmEnabled]);

  // Configuração do tipo de filtro atual
  const currentConfig = useMemo(() => {
    return availableFilterTypes.find(c => c.type === filter.type);
  }, [availableFilterTypes, filter.type]);

  // Operadores disponíveis para o tipo atual
  const availableOperators = useMemo(() => {
    if (!currentConfig) return [];
    return OPERATORS_BY_VALUE_TYPE[currentConfig.valueType];
  }, [currentConfig]);

  // Opções de valor para o tipo de filtro atual
  const valueOptions = useMemo((): FilterOption[] => {
    switch (filter.type) {
      case 'contact_tag':
        return options.contactTags;
      case 'contact_seller':
        return options.contactSellers;
      case 'conversation_status':
        return options.conversationStatuses;
      case 'conversation_inbox':
        return options.inboxes;
      case 'conversation_department':
        return options.departments;
      case 'conversation_agent':
        return options.agents;
      case 'conversation_phase':
        return options.conversationPhases;
      case 'deal_status':
        return options.dealStatuses;
      case 'deal_tag':
        return options.dealTags;
      case 'deal_phase':
        return options.dealPhases;
      case 'deal_workspace':
        return options.workspaces;
      case 'deal_source':
        return options.leadSources;
      case 'deal_seller':
        return options.dealSellers;
      default:
        return [];
    }
  }, [filter.type, options]);

  // Agrupar tipos de filtros por categoria para o select
  const groupedFilterTypes = useMemo(() => {
    const groups: Record<string, FilterTypeConfig[]> = {
      contact: [],
      conversation: [],
      deal: [],
    };
    
    availableFilterTypes.forEach(config => {
      groups[config.category].push(config);
    });
    
    return groups;
  }, [availableFilterTypes]);

  /**
   * Handler para mudança de tipo de filtro
   */
  const handleTypeChange = (newType: SegmentFilterType) => {
    const newConfig = availableFilterTypes.find(c => c.type === newType);
    const newOperators = newConfig ? OPERATORS_BY_VALUE_TYPE[newConfig.valueType] : [];
    
    onChange({
      ...filter,
      type: newType,
      operator: newOperators[0] || 'is_any',
      value: [], // Reset values ao mudar tipo
    });
  };

  /**
   * Handler para mudança de operador
   */
  const handleOperatorChange = (newOperator: SegmentOperator) => {
    onChange({
      ...filter,
      operator: newOperator,
    });
  };

  /**
   * Handler para toggle de valor (multi-select)
   */
  const handleValueToggle = (value: string) => {
    const isMulti = currentConfig?.valueType === 'multi' || currentConfig?.valueType === 'status';
    
    if (isMulti) {
      const newValues = filter.value.includes(value)
        ? filter.value.filter(v => v !== value)
        : [...filter.value, value];
      onChange({ ...filter, value: newValues });
    } else {
      // Single select - substitui o valor
      onChange({ ...filter, value: [value] });
    }
  };

  /**
   * Remove um valor específico
   */
  const handleRemoveValue = (value: string) => {
    onChange({
      ...filter,
      value: filter.value.filter(v => v !== value),
    });
  };

  // Labels das categorias para o select agrupado
  const categoryLabels: Record<string, string> = {
    contact: '📇 Contato',
    conversation: '💬 Conversa',
    deal: '💼 Negócio (CRM)',
  };

  return (
    <div className="flex flex-wrap items-center gap-2 p-3 bg-muted/30 rounded-lg border">
      {/* Seletor de tipo de filtro */}
      <Select
        value={filter.type}
        onValueChange={(v) => handleTypeChange(v as SegmentFilterType)}
      >
        <SelectTrigger className="w-[180px]">
          <SelectValue placeholder="Selecione o filtro" />
        </SelectTrigger>
        <SelectContent>
          {Object.entries(groupedFilterTypes).map(([category, types]) => {
            if (types.length === 0) return null;
            return (
              <SelectGroup key={category}>
                <SelectLabel>{categoryLabels[category]}</SelectLabel>
                {types.map(config => (
                  <SelectItem key={config.type} value={config.type}>
                    {config.label}
                  </SelectItem>
                ))}
              </SelectGroup>
            );
          })}
        </SelectContent>
      </Select>

      {/* Seletor de operador */}
      <Select
        value={filter.operator}
        onValueChange={(v) => handleOperatorChange(v as SegmentOperator)}
      >
        <SelectTrigger className="w-[150px]">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {availableOperators.map(op => (
            <SelectItem key={op} value={op}>
              {OPERATOR_LABELS[op]}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      {/* Seletor de valor(es) */}
      <Select
        value=""
        onValueChange={handleValueToggle}
      >
        <SelectTrigger className="w-[200px]">
          <SelectValue placeholder={currentConfig?.placeholder || 'Selecione...'} />
        </SelectTrigger>
        <SelectContent>
          {valueOptions.length === 0 ? (
            <div className="p-2 text-sm text-muted-foreground text-center">
              Nenhuma opção disponível
            </div>
          ) : (
            valueOptions.map(option => (
              <SelectItem 
                key={option.value} 
                value={option.value}
                disabled={filter.value.includes(option.value)}
              >
                <div className="flex items-center gap-2">
                  {option.color && (
                    <div 
                      className="w-3 h-3 rounded-full" 
                      style={{ backgroundColor: option.color }}
                    />
                  )}
                  {option.label}
                  {filter.value.includes(option.value) && (
                    <span className="text-primary ml-auto">✓</span>
                  )}
                </div>
              </SelectItem>
            ))
          )}
        </SelectContent>
      </Select>

      {/* Valores selecionados como badges */}
      {filter.value.length > 0 && (
        <div className="flex flex-wrap gap-1 ml-2">
          {filter.value.map(val => {
            const option = valueOptions.find(o => o.value === val);
            return (
              <Badge 
                key={val} 
                variant="secondary"
                className={cn(
                  "gap-1 pr-1",
                  option?.color && "border-l-4"
                )}
                style={option?.color ? { borderLeftColor: option.color } : undefined}
              >
                {option?.label || val}
                <button
                  type="button"
                  onClick={() => handleRemoveValue(val)}
                  className="ml-1 hover:bg-muted rounded p-0.5"
                >
                  <X className="h-3 w-3" />
                </button>
              </Badge>
            );
          })}
        </div>
      )}

      {/* Botão remover filtro */}
      <Button
        type="button"
        variant="ghost"
        size="icon"
        onClick={onRemove}
        disabled={isOnlyFilter}
        className="ml-auto shrink-0"
      >
        <X className="h-4 w-4" />
      </Button>
    </div>
  );
}
